import { createSelector } from 'reselect'
import T from 'i18n-react'
import * as R from 'ramda'
import {
  createSelectorWithPath,
  labelSelectorFunc,
  entitiesSelector
} from 'helpers/ComponentSelectorUtil'
import { formFilterPairs } from 'components/custom/AddPopup/AddPopupUtil'

const sourcesSelectorFunc = state => state.sources
const valuesSelectorFunc = state => state.values
const popupSelectorFunc = state => state.togglePopup
const configSelectionSelectorFunc = state => state.configSelection
const configSourceSelectorFunc = state => state.configSource
const vendorNameSelectorFunc = state => state.vendorNameFilter
const partNumberSelectorFunc = state => state.partNumberFilter
const partTypeSelectorFunc = state => state.partTypeFilter
const phaseCountSelectorFunc = state => state.phaseCountFilter
const efficiencySelectorFunc = state => state.efficiencyFilter
const curConfigSelectorFunc = state => state.curConfig
const selectedFilterSelectorFunc = state => state.selectedFilter
const hintSelectorFunc = state => (state.defaultValue) ? T.translate(state.defaultValue) : ''
const isEditSelectorFunc =  state => state.isEditPart
const isAddPartSelectorFunc = state => state.isAddPart

const sourcesSelectorHoc = (sourcesSelector) => (
  createSelector(
    entitiesSelector,
    sourcesSelector,
    (entities, refs) => refs.map(({id, type}) => {
      const source = entities[type][id]
      return { ...source }
    })
  )
)

const valuesSelectorHoc = (valuesSelector) => (
  createSelector(
    valuesSelector,
    (values) => {
      // skip selection if values is empty
      if (values.length === 0) return []

      const allParts = Array.from(values)
      let configArr = [[allParts.shift()]]

      allParts.forEach(part => {
        // flag if the configuration of the part exists
        let configExists = false

        // find corresponding configuration for the part and update current configuration array
        configArr = configArr.map(partArray => {
          // use the first part of the part array of current configuration
          const config = partArray[0]

          if (part.railType === config.railType) {
            partArray.push(part)
            configExists = true
          }

          return partArray
        })

        // if configuration does not exist for this part, create a new configuration and
        // push the part in
        if (!configExists) {
          configArr.push([part])
        }
      })

      // remove PEXVDD
      return configArr.filter(config => config[0].railType !== 'PEXVDD')
    }
  )
)

const edpPeakSelectorHoc = (valuesSelector) => (
  createSelector(
    valuesSelector,
    (values) => values[0] ? values[0].edpPeak : null
  )
)

const edpContinuousSelectorHoc = (valuesSelector) => (
  createSelector(
    valuesSelector,
    (values) => values[0] ? values[0].edpContinuous : null
  )
)

const outputVoltageSelectorHoc = (valuesSelector) => (
  createSelector(
    valuesSelector,
    (values) => values[0] ? values[0].outputVoltage : null
  )
)

const filterPairsSelectorHoc = (sourcesSelector) => (
  createSelector(
    sourcesSelector,
    (sources) => formFilterPairs({
      vendorName: 'Vendor Name',
      partType: 'Part Type',
      partNumber: 'Part Number',
      phaseCount: 'Phase Count',
      efficiency: 'VR Efficiency (%)'
    }, sources, {})
  )
)

export default (pathName, uiPath) => {
  const withPath = createSelectorWithPath(pathName)
  const withUiPath = createSelectorWithPath(uiPath)

  const labelSelector = withPath(labelSelectorFunc)
  const sourcesSelectorRef = withPath(sourcesSelectorFunc)
  const valuesSelector = withPath(valuesSelectorFunc)

  const sourcesSelector = sourcesSelectorHoc(sourcesSelectorRef)
  const voltageConfigsSelector = valuesSelectorHoc(valuesSelector)
  const edpPeakSelector = edpPeakSelectorHoc(valuesSelector)
  const edpContinuousSelector = edpContinuousSelectorHoc(valuesSelector)
  const outputVoltageSelector = outputVoltageSelectorHoc(valuesSelector)

  const togglePopupSelector = withUiPath(popupSelectorFunc)
  const configSelectionSelector = withUiPath(configSelectionSelectorFunc)
  const configSourceSelector = withUiPath(configSourceSelectorFunc)
  const vendorNameSelector = withUiPath(vendorNameSelectorFunc)
  const partNumberSelector = withUiPath(partNumberSelectorFunc)
  const partTypeSelector = withUiPath(partTypeSelectorFunc)
  const phaseCountSelector = withUiPath(phaseCountSelectorFunc)
  const efficiencySelector = withUiPath(efficiencySelectorFunc)
  const curConfigSelector = withUiPath(curConfigSelectorFunc)
  const selectedFilterSelector = withUiPath(selectedFilterSelectorFunc)

  const filterPairsSelector = filterPairsSelectorHoc(sourcesSelector)

  const hintSelector = withPath(hintSelectorFunc)
  const isEditSelector = withUiPath(isEditSelectorFunc)
  const isAddPartSelector = withUiPath(isAddPartSelectorFunc)

  return {
    labelSelector,
    sourcesSelector,
    voltageConfigsSelector,
    edpPeakSelector,
    edpContinuousSelector,
    outputVoltageSelector,
    configSelectionSelector,
    configSourceSelector,
    togglePopupSelector,
    vendorNameSelector,
    partNumberSelector,
    partTypeSelector,
    phaseCountSelector,
    efficiencySelector,
    curConfigSelector,
    selectedFilterSelector,
    filterPairsSelector,
    valuesSelector,
    hintSelector,
    isEditSelector,
    isAddPartSelector
  }
}
